import { Component } from '@angular/core';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { RoomsFacade } from '@library/store/rooms/rooms.facade';
import { Room } from '@library/store/rooms/rooms.interface';
import { RoomsAssets } from '@library/utils/constants/rooms';
import { LangPipe } from '@library/utils/pipes/lang.pipe';
import { ControlPageAssistantService } from '@pages/control-page/control-page-assistant/control-page-assistant.service';
import { SUPPORTED_CONTROL_WEATHER_MODULES } from '@pages/control-page/control-page.const';
import { Enums } from 'constants/Enums.constant';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-control-page-assistant',
  templateUrl: './control-page-assistant.component.html',
  styleUrls: ['./control-page-assistant.component.scss'],
  providers: [ControlPageAssistantService]
})
export class ControlPageAssistantComponent {

  constructor(
    private assistantService: ControlPageAssistantService,
    private langPipe: LangPipe,
    private modulesfacade: ModulesFacade,
    private roomsFacade: RoomsFacade,
  ) {}

  /**
   * This is the algotithms that creates rooms and places modules that are not placed already
   * Spec is here : https://netatmo.atlassian.net/wiki/spaces/FRON/pages/4091970354/Unplaced+Weather+Products
   */
  async ngOnInit() {
    const homeModules = await firstValueFrom(this.modulesfacade.currentHomeModules$);
    const unplacedProducts = homeModules.filter(m => !m.room_id);
    const rooms = await firstValueFrom(this.roomsFacade.currentHomeRooms$);

    // Place all main modules in their own new room

    // For main modules with a name create a rooms with the name of the main module
    const namedIndoorModules = unplacedProducts
      .filter(m => ([Enums.ModuleType.NAMAIN, Enums.ModuleType.NAMODULE4].includes(m.type)) && m.name)
      .map((m) => ({
        room: {
          id: this.assistantService.uniqueRoomId(),
          type: RoomsAssets.livingroom.type,
          name: m.name,
        } as Room,
        modules: [m],
      }));

    // For main modules without a name, use 'living room' and add index ("- X") if multiple
    const nbLvingRooms = rooms.filter(r => r.name === this.langPipe.transform(RoomsAssets.livingroom.name)).length;
    const noNameIndoorModules = unplacedProducts
      .filter(m => ([Enums.ModuleType.NAMAIN, Enums.ModuleType.NAMODULE4].includes(m.type)) && !m.name)
      .map((m, index, arr) => ({
        room: {
          id: this.assistantService.uniqueRoomId(),
          type: RoomsAssets.livingroom.type,
          name: this.langPipe.transform(RoomsAssets.livingroom.name) +
            (arr.length > 1 ? ` - ${nbLvingRooms + index + 1}` : ''),
        } as Room,
        modules: [m],
      }));

    const indoorModules = [...namedIndoorModules, ...noNameIndoorModules];

    // Get all outdoor modules that are not already placed and grou them by their bridged main module
    const outdoorModules = homeModules.filter(m => m.type === Enums.ModuleType.NAMAIN)
      .map(weatherstation => {
        return (weatherstation.modules_bridged ?? [])
          .map(id => unplacedProducts.find(p => p.id === id))
          .filter(m => !!m && [Enums.ModuleType.NAMODULE1, Enums.ModuleType.NAMODULE2, Enums.ModuleType.NAMODULE3].includes(m.type));
      })
      .filter(modules => modules.length > 0);

    const outdoorRooms = rooms.filter(r => r.type === Enums.RoomTypes.OUTDOOR);
    const hasOutdoorRooms = outdoorRooms.length > 0;

    const newOutdoorRooms = [];

    // If there is no existing outdoor rooms, create a outdoor room for each group of outdoor modules
    if (!hasOutdoorRooms) {
      outdoorModules.forEach(modules => {
        newOutdoorRooms.push({
          room: {
            id: this.assistantService.uniqueRoomId(),
            type: Enums.RoomTypes.OUTDOOR,
            name: this.langPipe.transform(RoomsAssets.outdoor.name),
          },
          modules: modules,
        });
      });
    }
    else {
      const alreadyUsedRooms = [];
      outdoorModules.forEach(modules => {
        /** If a room exist and has already a outdoor module belonging to the same group than
         * groups we are trying to place, use that room */

        const mainModule = homeModules.find(m => m.id === modules[0].bridge);
        const room = outdoorRooms.find(room => room.module_ids?.some(id => mainModule.modules_bridged.includes(id)) ?? true);
        if (room) {
          newOutdoorRooms.push({room: room, modules: modules});
        }
        else {
          /** If there is a room that has no weather module in it, use that room */
          const weatherModules = homeModules.filter(m => SUPPORTED_CONTROL_WEATHER_MODULES.includes(m.type)).map(m => m.id);
          const room = outdoorRooms.find(r => !alreadyUsedRooms.includes(r.id) && r.module_ids?.every(id => !weatherModules.includes(id) ?? true));
          if (room) {
            alreadyUsedRooms.push(room.id);
            newOutdoorRooms.push({room: room, modules: modules});
          }
          // Create a outdoor room for each group of outdoor modules
          else {
            newOutdoorRooms.push({
              room: {
                id: this.assistantService.uniqueRoomId(),
                type: Enums.RoomTypes.OUTDOOR,
                name: this.langPipe.transform(RoomsAssets.outdoor.name),
              },
              modules: modules,
            });
          }
        }
      });
    }

    this.assistantService.newRooms.next([
      ...indoorModules,
      ...newOutdoorRooms,
    ]);
  }
}
